import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ENDPOINTS } from '@shared/constants';
import { generateURL } from '@shared/utils';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EncryptionService {
  constructor(private http: HttpClient) {}
  private publicKey: string | null = null;

  async encrypt(data: any) {
    const pem = await this.getPublicKey();
    const encoder = new TextEncoder();

    // Generate AES key
    const aesKey = await crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256,
      },
      true,
      ['encrypt', 'decrypt']
    );

    // Export AES key
    const exportedAesKey = await crypto.subtle.exportKey('raw', aesKey);

    // Random IV
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Encrypt data
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      aesKey,
      encoder.encode(JSON.stringify(data))
    );

    // Import RSA public key
    const publicKey = await this.importPublicKey(pem);

    // Encrypt AES key using RSA-OAEP SHA-256
    const encryptedKey = await crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      publicKey,
      exportedAesKey
    );

    return {
      key: this.arrayBufferToBase64(encryptedKey),
      iv: this.arrayBufferToBase64(iv),
      data: this.arrayBufferToBase64(encryptedData),
    };
  }

  private async importPublicKey(pem: string): Promise<CryptoKey> {
    const pemContents = pem
      .replace('-----BEGIN PUBLIC KEY-----', '')
      .replace('-----END PUBLIC KEY-----', '')
      .replace(/\s/g, '');

    const binaryDer = Uint8Array.from(atob(pemContents), (char) =>
      char.charCodeAt(0)
    );

    return crypto.subtle.importKey(
      'spki',
      binaryDer.buffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['encrypt']
    );
  }

  private arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
    const bytes =
      buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);

    let binary = '';

    bytes.forEach((b) => {
      binary += String.fromCharCode(b);
    });

    return btoa(binary);
  }

  private async getPublicKey(): Promise<string> {
    if (this.publicKey) {
      return this.publicKey;
    }

    const url = generateURL({
      endpoint: ENDPOINTS.ENDPOINT_PUBLIC_KEY,
    });

    const response = await firstValueFrom(
      this.http.get<{ publicKey: string }>(url)
    );

    this.publicKey = response.publicKey;

    return response.publicKey;
  }
}
